import React, { Suspense, useCallback, useMemo, useRef, useState } from "react";
import { Dropdown, Modal } from "react-bootstrap";
import {
  IconEye,
  IconEyeOff,
  IconLayoutColumns,
  IconRefresh,
  IconSearch,
} from "@tabler/icons-react";
import FullLoader from "components/loader/full-loader";
import { useUser } from "contexts/v1/masterData/UserContext";
import { useDepartments } from "pages/hr/master/context/DepartmentsContext";
import { patchRequest, postRequest, deleteRequest } from "services/api";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import PermissionMiddleware from "middleware/PermissionMiddleware";
import CreatePage from "pages/user/components/create-page";
import { ProductCreateProvider } from "contexts/v1/masterData/ProductCreateContext";
import { useFileUpload } from "hooks/useFileUpload";
import { API_URL } from "utils/constants";
import Swal from "sweetalert2";
import "./staff-detail-tab.css";

const STATUS_OPTIONS = ["Trainee", "DW", "Kontrak", "Tetap"];
const GENDER_OPTIONS = ["L", "P"];

const STAFF_COLUMNS = [
  { key: "gender", label: "L/P", type: "select", options: GENDER_OPTIONS, width: "70px" },
  { key: "nik", label: "NIK", type: "text", width: "150px" },
  { key: "birth_details", label: "Tempat / Tgl Lahir", type: "birth_details", width: "190px" },
  { key: "address", label: "Alamat", type: "text", width: "200px" },
  { key: "phone", label: "No Tlp", type: "text", width: "120px" },
  { key: "department_id", label: "Departemen", type: "department_select", width: "150px" },
  { key: "work_start_date", label: "Tgl Bergabung", type: "date", width: "120px" },
  { key: "employee_status", label: "Status", type: "select", options: STATUS_OPTIONS, width: "100px" },
  { key: "nomor_rekening", label: "No Rekening", type: "text", width: "150px" },
  { key: "staff_note", label: "Catatan / Pelanggaran", type: "textarea", width: "240px" },
];

const pickValue = (item, keys) => {
  for (const key of keys) {
    if (item?.[key]) return item[key];
  }
  return "";
};

const pad2 = (value) => String(value).padStart(2, "0");

const normalizeDateForApi = (value) => {
  if (!value) return "";
  const rawValue = String(value).trim();
  if (!rawValue || rawValue === "-") return "";

  if (rawValue.includes("T")) {
    const parsedDate = new Date(rawValue);
    if (!Number.isNaN(parsedDate.getTime())) {
      return `${parsedDate.getFullYear()}-${pad2(parsedDate.getMonth() + 1)}-${pad2(parsedDate.getDate())}`;
    }
  }

  const slashMatch = rawValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, day, month, year] = slashMatch;
    return `${year}-${pad2(month)}-${pad2(day)}`;
  }

  const isoMatch = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

  return rawValue;
};

const isDateReadyForAutosave = (value) => {
  if (!value) return true;
  const rawValue = String(value).trim();
  return /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.test(rawValue) || /^(\d{4})-(\d{2})-(\d{2})/.test(rawValue);
};

const formatDateForDisplay = (value) => {
  const normalized = normalizeDateForApi(value);
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return normalized;
  return `${match[3]}/${match[2]}/${match[1]}`;
};

const getAgeFromDisplayDate = (value) => {
  const normalized = normalizeDateForApi(value);
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const birthDate = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }
  return Number.isFinite(age) ? age : null;
};

const getDepartmentName = (user) =>
  user?.department?.name ||
  user?.department_name ||
  user?.position?.department?.name ||
  "Tanpa Departemen";

const MaskedEmail = ({ email }) => {
  const [isHovered, setIsHovered] = useState(false);
  if (!email) return <span>-</span>;
  const maskEmail = (email) => {
    const parts = email.split('@');
    if (parts.length !== 2) return email;
    const [local, domain] = parts;
    const visibleLocal = Math.min(2, local.length);
    const maskedLocal = local.slice(0, visibleLocal) + '*'.repeat(Math.max(0, local.length - visibleLocal));
    const visibleDomain = Math.min(3, domain.length);
    const maskedDomain = '*'.repeat(Math.max(0, domain.length - visibleDomain)) + domain.slice(-visibleDomain);
    return `${maskedLocal}@${maskedDomain}`;
  };

  return (
    <span
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ cursor: 'pointer' }}
    >
      {isHovered ? email : maskEmail(email)}
    </span>
  );
};

const UploadPhotoComponent = ({ userId, onSuccess, onError }) => {
  const { file, FileUpload, FileSelectDisplay } = useFileUpload();

  const handleSubmit = async () => {
    if (!file) {
      onError("Please select a file");
      return;
    }
    const formData = new FormData();
    formData.append('photo', file);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/user/${userId}/photo`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          onSuccess();
        } else {
          onError("Failed to upload photo");
        }
      } else {
        onError("Failed to upload photo");
      }
    } catch (error) {
      onError("Error uploading photo");
    }
  };

  return (
    <div>
      <FileUpload />
      <FileSelectDisplay />
      <button className="btn btn-primary mt-3" onClick={handleSubmit}>
        Upload
      </button>
    </div>
  );
};

const StaffDetailTab = () => {
  const { users, refetch, handleSearch, filterOptions, handleFilter, clearFilters, Pagination } = useUser();
  const { departments } = useDepartments();
  const { handleSuccessToast, handleError, showCanvasHandler } = useCanvasHandler();
  
  const [visibleColumns, setVisibleColumns] = useState(() =>
    STAFF_COLUMNS.reduce((result, column) => ({ ...result, [column.key]: true }), {})
  );
  
  const [collapsedDepts, setCollapsedDepts] = useState({});
  const [editData, setEditData] = useState({});
  const [savedData, setSavedData] = useState({});
  const [savingRows, setSavingRows] = useState({});
  const autosaveTimersRef = useRef({});

  // Search & Modal states
  const [search, setSearch] = useState(filterOptions.search || "");
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showUserinfoModal, setShowUserinfoModal] = useState(false);
  const [userinfoPins, setUserinfoPins] = useState("");
  const [userinfoResponse, setUserinfoResponse] = useState(null);
  const [userinfoLoading, setUserinfoLoading] = useState(false);

  const toggleDept = (deptName) => {
    setCollapsedDepts((prev) => ({
      ...prev,
      [deptName]: !prev[deptName],
    }));
  };

  const groupedUsers = useMemo(() => {
    const groups = (users.data || []).reduce((result, user) => {
      const departmentName = getDepartmentName(user).toUpperCase();
      return {
        ...result,
        [departmentName]: [...(result[departmentName] || []), user],
      };
    }, {});

    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [users.data]);

  const activeColumns = STAFF_COLUMNS.filter((column) => visibleColumns[column.key]);

  const toggleColumn = (key) => {
    setVisibleColumns((current) => ({ ...current, [key]: !current[key] }));
  };

  const buildUserPayload = useCallback((user, changes) => {
    const payload = {
      name: changes.name ?? savedData[user.id]?.name ?? user.name,
      ...changes,
    };

    ["birth_date", "work_start_date", "join_date"].forEach((field) => {
      if (payload[field] !== undefined) {
        payload[field] = normalizeDateForApi(payload[field]);
      }
    });

    return payload;
  }, [savedData]);

  const autosaveUser = useCallback(async (user, changes) => {
    if (!changes || !Object.keys(changes).length) return;
    setSavingRows((prev) => ({ ...prev, [user.id]: true }));
    try {
      const payload = buildUserPayload(user, changes);
      const response = await patchRequest(`/user/${user.id}`, payload);
      if (response.success) {
        setSavedData((prev) => ({
          ...prev,
          [user.id]: {
            ...(prev[user.id] || {}),
            ...payload,
            birth_date: payload.birth_date !== undefined ? formatDateForDisplay(payload.birth_date) : prev[user.id]?.birth_date,
            work_start_date: payload.work_start_date !== undefined ? formatDateForDisplay(payload.work_start_date) : prev[user.id]?.work_start_date,
          },
        }));
        setEditData((prev) => {
          const next = { ...prev };
          if (next[user.id] === changes) {
            delete next[user.id];
          } else if (next[user.id]) {
            const remaining = { ...next[user.id] };
            Object.keys(changes).forEach((key) => {
              if (remaining[key] === changes[key]) delete remaining[key];
            });
            if (Object.keys(remaining).length) next[user.id] = remaining;
            else delete next[user.id];
          }
          return next;
        });
      } else {
        handleError("Gagal menyimpan data staff");
      }
    } catch (err) {
      handleError("Terjadi kesalahan saat menyimpan data");
    } finally {
      setSavingRows((prev) => {
        const next = { ...prev };
        delete next[user.id];
        return next;
      });
    }
  }, [buildUserPayload, handleError]);

  const scheduleAutosave = useCallback((user, nextChanges, delay = 700) => {
    if (autosaveTimersRef.current[user.id]) {
      clearTimeout(autosaveTimersRef.current[user.id]);
    }

    autosaveTimersRef.current[user.id] = setTimeout(() => {
      delete autosaveTimersRef.current[user.id];
      autosaveUser(user, nextChanges);
    }, delay);
  }, [autosaveUser]);

  const handleInputChange = (user, field, value, options = {}) => {
    const userId = user.id;
    const nextChanges = {
      ...(editData[userId] || {}),
      [field]: value
    };

    setEditData((prev) => ({
      ...prev,
      [userId]: {
        ...(prev[userId] || {}),
        [field]: value
      }
    }));

    if (options.dateField && !isDateReadyForAutosave(value)) return;

    scheduleAutosave(user, nextChanges, options.immediate ? 150 : 700);
  };

  const getFieldValue = (user, key) => {
    if (editData[user.id] && editData[user.id][key] !== undefined) {
      return editData[user.id][key];
    }
    if (savedData[user.id] && savedData[user.id][key] !== undefined) {
      return savedData[user.id][key];
    }
    
    switch(key) {
      case "name": return user.name || "";
      case "gender": return pickValue(user, ["gender", "sex"]).toUpperCase() === "MALE" || pickValue(user, ["gender", "sex"]).toUpperCase() === "L" ? "L" : pickValue(user, ["gender", "sex"]).toUpperCase() === "FEMALE" || pickValue(user, ["gender", "sex"]).toUpperCase() === "P" ? "P" : "";
      case "nik": return pickValue(user, ["nik", "identity_number", "id_number", "ktp_number"]);
      case "birth_place": return pickValue(user, ["birth_place", "place_of_birth", "tempat_lahir"]);
      case "birth_date": {
        const bd = pickValue(user, ["birth_date", "date_of_birth", "tgl_lahir"]);
        return bd && bd !== "-" ? formatDateForDisplay(bd) : "";
      }
      case "birth_details": return `${getFieldValue(user, "birth_place")}${getFieldValue(user, "birth_date") ? `, ${getFieldValue(user, "birth_date")}` : ""}`;
      case "address": return pickValue(user, ["address", "alamat", "domicile_address"]);
      case "phone": return pickValue(user, ["phone", "phone_number", "mobile"]);
      case "department_id": return user.department_id || user.department?.id || "";
      case "work_start_date": {
        const wd = pickValue(user, ["work_start_date", "start_work_date", "join_date", "joined_at"]);
        return wd && wd !== "-" ? formatDateForDisplay(wd) : "";
      }
      case "employee_status": return pickValue(user, ["employee_status", "employment_status", "status"]);
      case "nomor_rekening": return pickValue(user, ["nomor_rekening", "bank_account_number", "account_number"]);
      case "staff_note": return pickValue(user, ["staff_note", "violation_note", "violation_notes", "notes", "note"]);
      default: return user[key] || "";
    }
  };

  // User Page Handlers
  const handleSearchInput = (e) => {
    if (e.key === "Enter") {
      if (!search.trim()) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Search input cannot be empty!",
          showConfirmButton: false,
          timer: 3000,
        });
      } else {
        handleSearch(search);
      }
    }
  };

  const handleCreate = (e) => {
    e.preventDefault();
    showCanvasHandler(
      "Create User",
      <ProductCreateProvider>
        <CreatePage
          autoRole="user_hr"
          onSuccess={() => {
            handleSuccessToast(true, "User creating");
            refetch();
          }}
          onError={handleError}
        />
      </ProductCreateProvider>
    );
  };

  const handleUpdate = (e, data) => {
    e.preventDefault();
    showCanvasHandler(
      "Update User",
      <ProductCreateProvider>
        <CreatePage
          initialData={data}
          onSuccess={() => {
            handleSuccessToast(true, "User updating");
            refetch();
          }}
          onError={handleError}
        />
      </ProductCreateProvider>
    );
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const response = await deleteRequest(`/user/${id}`);
        if (response.success) {
          handleSuccessToast(true, "User deleting");
          refetch();
        }
      }
    });
  };

  const handleSync = async (userId) => {
    try {
      const response = await postRequest("/ahr/fingerspot/sync-users", { user_ids: [userId] });
      if (response.success) {
        handleSuccessToast(true, "User synced successfully");
      } else {
        handleError("Failed to sync user");
      }
    } catch (error) {
      handleError("Error syncing user");
    }
  };

  const handleSyncAll = async () => {
    const userIds = users.data.map(user => user.id);
    try {
      const response = await postRequest("/ahr/fingerspot/sync-users", { user_ids: userIds });
      if (response.success) {
        handleSuccessToast(true, "All users synced successfully");
      } else {
        handleError("Failed to sync users");
      }
    } catch (error) {
      handleError("Error syncing users");
    }
  };

  const handleDeleteFingerspot = async (userData) => {
    if (!userData.fingerspot_pin) {
      handleError("User does not have a Fingerspot PIN");
      return;
    }
    try {
      const response = await postRequest("/ahr/fingerspot/delete-users", { pins: [userData.fingerspot_pin.toString()] });
      if (response.success) {
        handleSuccessToast(true, "User deleted from Fingerspot successfully");
        refetch();
      } else {
        handleError("Failed to delete user from Fingerspot");
      }
    } catch (error) {
      handleError("Error deleting user from Fingerspot");
    }
  };

  const handlePhotoClick = (photoUrl) => {
    setSelectedPhoto(photoUrl);
    setShowPhotoModal(true);
  };

  const handleUploadPhoto = (userData) => {
    showCanvasHandler(
      "Upload Photo",
      <UploadPhotoComponent
        userId={userData.id}
        onSuccess={() => {
          handleSuccessToast(true, "Photo uploaded successfully");
          refetch();
        }}
        onError={handleError}
      />
    );
  };

  const handleGetUserinfo = async () => {
    setUserinfoLoading(true);
    setUserinfoResponse(null);
    try {
      let pinsArray = [];
      if (userinfoPins.trim()) {
        pinsArray = userinfoPins.split(',').map(pin => pin.trim()).filter(pin => pin !== '');
      }
      const requestBody = pinsArray.length > 0 ? { pins: pinsArray } : {};
      const response = await postRequest("/ahr/fingerspot/get-userinfo", requestBody);
      setUserinfoResponse(response);
    } catch (error) {
      setUserinfoResponse({ error: true, message: error.message || "Failed to get userinfo" });
    } finally {
      setUserinfoLoading(false);
    }
  };

  if (users.loading || departments.loading) return <FullLoader />;
  if (users.error) return <Suspense error={users.error} />;

  const filterOptions_list = [
    { value: "", label: "All" },
    { value: "1", label: "Registered" },
    { value: "0", label: "Not Registered" },
  ];
  const hasActiveFilters = filterOptions.fingerspot_finger || filterOptions.fingerspot_face || filterOptions.fingerspot_vein || filterOptions.department_id;

  return (
    <>
      <div className="staff-detail-tab staff-detail-page">
      <div className="p-3">
        <div className="d-flex justify-content-end mb-4 g-3">
            <div className="btn-list">
              <Dropdown>
                <Dropdown.Toggle variant="light" className="border">
                  <IconLayoutColumns size={17} className="me-1" /> Columns
                </Dropdown.Toggle>
                <Dropdown.Menu className="p-2" style={{ minWidth: "230px" }}>
                  {STAFF_COLUMNS.map((column) => (
                    <label className="dropdown-item d-flex align-items-center gap-2" key={column.key}>
                      <input
                        type="checkbox"
                        className="form-check-input m-0"
                        checked={visibleColumns[column.key]}
                        onChange={() => toggleColumn(column.key)}
                      />
                      {visibleColumns[column.key] ? <IconEye size={15} /> : <IconEyeOff size={15} />}
                      <span>{column.label}</span>
                    </label>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              <button className="btn btn-light border" onClick={refetch}>
                <IconRefresh size={17} className="me-1" /> Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Top Controls: Search, Filters, Create, Sync */}
        <div className="d-flex flex-wrap gap-2 align-items-center mb-3 p-3 bg-white border rounded shadow-sm">
          <div className="input-icon">
            <input
              type="text"
              className="form-control"
              placeholder="Cari staff..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearchInput}
            />
            <span className="input-icon-addon">
              <IconSearch size={16} />
            </span>
          </div>

          <select
            className="form-select"
            style={{ width: 'auto', minWidth: '150px' }}
            value={filterOptions.department_id || ""}
            onChange={(e) => handleFilter('department_id', e.target.value)}
          >
            <option value="">Semua Department</option>
            {departments.data?.map((dept) => (
              <option key={dept.id} value={dept.id}>{dept.name}</option>
            ))}
          </select>

          <select className="form-select" style={{ width: 'auto' }} value={filterOptions.fingerspot_finger || ""} onChange={(e) => handleFilter('fingerspot_finger', e.target.value)}>
            <option value="">Fingerspot: Finger</option>
            {filterOptions_list.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          <select className="form-select" style={{ width: 'auto' }} value={filterOptions.fingerspot_face || ""} onChange={(e) => handleFilter('fingerspot_face', e.target.value)}>
            <option value="">Fingerspot: Face</option>
            {filterOptions_list.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          <select className="form-select" style={{ width: 'auto' }} value={filterOptions.fingerspot_vein || ""} onChange={(e) => handleFilter('fingerspot_vein', e.target.value)}>
            <option value="">Fingerspot: Vein</option>
            {filterOptions_list.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>

          {hasActiveFilters && (
            <button className="btn btn-outline-secondary btn-sm" onClick={clearFilters} title="Clear Filters">Reset Filter</button>
          )}

          <div className="ms-auto d-flex gap-2 flex-wrap">
            <button className="btn btn-primary" onClick={handleCreate}>
              + Tambah Staff
            </button>
            <button className="btn btn-secondary" onClick={handleSyncAll}>
              Sync Semua User
            </button>
            <button 
              className="btn btn-info" 
              onClick={() => {
                setUserinfoPins("");
                setUserinfoResponse(null);
                setShowUserinfoModal(true);
              }}
            >
              Get User Info
            </button>
          </div>
        </div>

        <div className="card border-0 shadow-sm overflow-hidden mb-3">
          <div className="table-responsive staff-detail-scroll">
            <table className="table table-vcenter staff-detail-table mb-0 table-hover align-middle">
              <thead>
                <tr>
                  <th className="staff-no-col text-center" style={{ width: "40px" }}>NO</th>
                  <th className="staff-name-col" style={{ minWidth: "150px" }}>NAMA</th>
                  {activeColumns.map((column) => (
                    <th key={column.key} style={{ minWidth: column.width }}>{column.label}</th>
                  ))}
                  <th className="text-center" style={{ width: "80px", position: "sticky", right: 0, backgroundColor: "#fff", zIndex: 1 }}>AKSI</th>
                </tr>
              </thead>
              <tbody>
                {groupedUsers.length ? groupedUsers.map(([departmentName, departmentUsers]) => {
                  const isCollapsed = collapsedDepts[departmentName];
                  return (
                    <React.Fragment key={departmentName}>
                      <tr style={{ cursor: "pointer", backgroundColor: "#f8f9fa" }} onClick={() => toggleDept(departmentName)}>
                        <td className="staff-section-spacer border-end-0" />
                        <td className="staff-section border-start-0" colSpan={activeColumns.length + 2}>
                          <div className="d-flex align-items-center gap-2 fw-bold text-primary">
                            {isCollapsed ? (
                              <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-chevron-right" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <polyline points="9 6 15 12 9 18"></polyline>
                              </svg>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-chevron-down" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            )}
                            {departmentName} ({departmentUsers.length})
                          </div>
                        </td>
                      </tr>
                      {!isCollapsed && departmentUsers.map((user, index) => {
                        const hasChanges = editData[user.id] && Object.keys(editData[user.id]).length > 0;
                        const isSaving = Boolean(savingRows[user.id]);

                        return (
                          <tr key={user.id} className={hasChanges ? "bg-primary-lt" : ""}>
                            <td className="staff-no-col text-center">{index + 1}</td>
                            <td className="staff-name-col">
                              <input
                                type="text"
                                className="form-control form-control-sm fw-bold"
                                value={getFieldValue(user, "name")}
                                onChange={(e) => handleInputChange(user, "name", e.target.value)}
                                placeholder="Nama Karyawan"
                              />
                            </td>
                            {activeColumns.map((column) => (
                              <td key={column.key} className="p-1">
                                {column.type === "select" && (
                                  <select
                                    className="form-select form-select-sm"
                                    value={getFieldValue(user, column.key)}
                                    onChange={(e) => handleInputChange(user, column.key, e.target.value, { immediate: true })}
                                  >
                                    <option value="">-</option>
                                    {column.options.map((opt) => (
                                      <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                  </select>
                                )}
                                {column.type === "department_select" && (
                                  <select
                                    className="form-select form-select-sm"
                                    value={getFieldValue(user, column.key)}
                                    onChange={(e) => handleInputChange(user, column.key, e.target.value, { immediate: true })}
                                  >
                                    <option value="">- Pilih Dept -</option>
                                    {departments?.data?.map((dept) => (
                                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                                    ))}
                                  </select>
                                )}
                                {column.type === "text" && (
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={getFieldValue(user, column.key)}
                                    onChange={(e) => handleInputChange(user, column.key, e.target.value)}
                                    placeholder="-"
                                  />
                                )}
                                {column.type === "textarea" && (
                                  <textarea
                                    className="form-control form-control-sm"
                                    rows={3}
                                    value={getFieldValue(user, column.key)}
                                    onChange={(e) => handleInputChange(user, column.key, e.target.value)}
                                    placeholder="Catatan pelanggaran / evaluasi"
                                  />
                                )}
                                {column.type === "birth_details" && (
                                  <div className="d-flex flex-column gap-1">
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={getFieldValue(user, "birth_place")}
                                      onChange={(e) => handleInputChange(user, "birth_place", e.target.value)}
                                      placeholder="Tempat lahir"
                                    />
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      className="form-control form-control-sm"
                                      value={getFieldValue(user, "birth_date")}
                                      onChange={(e) => handleInputChange(user, "birth_date", e.target.value, { dateField: true })}
                                      placeholder="dd/mm/yyyy"
                                    />
                                    {getAgeFromDisplayDate(getFieldValue(user, "birth_date")) !== null && (
                                      <small className="text-muted" style={{ fontSize: "11px" }}>
                                        Umur: {getAgeFromDisplayDate(getFieldValue(user, "birth_date"))} tahun
                                      </small>
                                    )}
                                  </div>
                                )}
                                {column.type === "date" && (
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    className="form-control form-control-sm"
                                    value={getFieldValue(user, column.key)}
                                    onChange={(e) => handleInputChange(user, column.key, e.target.value, { dateField: true })}
                                    placeholder="dd/mm/yyyy"
                                  />
                                )}
                                {column.type === "photo" && (
                                  <div className="text-center py-1">
                                    {user.photo_url ? (
                                      <img
                                        src={user.photo_url}
                                        alt="User"
                                        style={{ width: '32px', height: '32px', objectFit: 'cover', cursor: 'pointer', borderRadius: '4px' }}
                                        onClick={() => handlePhotoClick(user.photo_url)}
                                      />
                                    ) : (
                                      <span className="text-muted small">No Photo</span>
                                    )}
                                  </div>
                                )}
                                {column.type === "readonly" && (
                                  <div className="px-2 py-1 text-muted text-center">{user[column.key] || '-'}</div>
                                )}
                                {column.type === "email" && (
                                  <div className="px-2 py-1"><MaskedEmail email={user.email} /></div>
                                )}
                                {column.type === "badge" && (
                                  <div className="text-center py-1">
                                    <span className="badge" style={{ 
                                      backgroundColor: user[column.key] > 0 ? '#22c55e' : '#ef4444',
                                      color: 'white',
                                      padding: '0.4rem 0.6rem',
                                      borderRadius: '0.375rem',
                                    }}>
                                      {user[column.key] > 0 ? '✓' : '✗'}
                                    </span>
                                  </div>
                                )}
                              </td>
                            ))}
                            <td className="text-center p-1" style={{ position: "sticky", right: 0, backgroundColor: hasChanges ? "var(--tblr-primary-lt)" : "#fff", zIndex: 1 }}>
                              {isSaving ? (
                                <span className="staff-autosave-status">
                                  <span className="spinner-border spinner-border-sm me-1" />
                                  Auto save
                                </span>
                              ) : (
                                <Dropdown>
                                  <Dropdown.Toggle variant="light" size="sm" className="w-100 border-0 shadow-none text-muted">
                                    Aksi
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu className="dropdown-menu-end">
                                    <Dropdown.Item onClick={(e) => handleUpdate(e, user)}>Edit Form Lengkap</Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleUploadPhoto(user)}>Upload Photo</Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleSync(user.id)}>Sync Fingerspot</Dropdown.Item>
                                    <PermissionMiddleware allowedPermissions={["hr_delete_fingerspot"]}>
                                      <Dropdown.Item className="text-warning" onClick={() => handleDeleteFingerspot(user)}>Delete Fingerspot</Dropdown.Item>
                                    </PermissionMiddleware>
                                    <PermissionMiddleware allowedPermissions={["hr_delete_user"]}>
                                      <Dropdown.Item className="text-danger" onClick={() => handleDelete(user.id)}>Hapus Karyawan</Dropdown.Item>
                                    </PermissionMiddleware>
                                  </Dropdown.Menu>
                                </Dropdown>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                }) : (
                  <tr>
                    <td colSpan={activeColumns.length + 3} className="text-center py-5">
                      Belum ada data staff.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {users.data.length > 0 && <div className="mt-3">{Pagination}</div>}
      </div>

      {/* Modals */}
      <Modal show={showPhotoModal} onHide={() => setShowPhotoModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>User Photo</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <img src={selectedPhoto} alt="User" style={{ maxWidth: '100%', maxHeight: '70vh' }} />
        </Modal.Body>
      </Modal>
      
      <Modal show={showUserinfoModal} onHide={() => setShowUserinfoModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Get User Info from Fingerspot</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">PINs (comma separated)</label>
            <textarea
              className="form-control"
              rows={3}
              placeholder='e.g., 61,62,63,64,65'
              value={userinfoPins}
              onChange={(e) => setUserinfoPins(e.target.value)}
            />
            <small className="text-muted">Enter PINs separated by commas.</small>
          </div>
          
          <div className="d-flex gap-2 mb-3">
            <button className="btn btn-primary" onClick={handleGetUserinfo} disabled={userinfoLoading}>
              {userinfoLoading ? 'Loading...' : 'Get User Info'}
            </button>
            <button className="btn btn-outline-secondary" onClick={() => setUserinfoPins("")}>Clear</button>
          </div>

          {userinfoResponse && (
            <div className="mt-3">
              <label className="form-label">Response:</label>
              <pre style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', maxHeight: '400px', overflow: 'auto', fontSize: '12px' }}>
                {JSON.stringify(userinfoResponse, null, 2)}
              </pre>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setShowUserinfoModal(false)}>Close</button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default StaffDetailTab;
